// missionScheduleUtils.js

import { MISSION_TASK_HANDLED_BY } from 'missions/taskmodal/missionTaskModalUtils';

export const MS_PER_MINUTE = 60 * 1000;
export const MS_PER_HOUR = 60 * MS_PER_MINUTE;
export const MS_PER_DAY = 24 * MS_PER_HOUR;
export const MS_PER_WEEK = 7 * MS_PER_DAY;
export const MS_PER_MONTH = 30 * MS_PER_DAY;

export const MISSION_DURATION_UNITS = Object.freeze([
  { label: 'Minutes', minutesMultiplier: 1, value: 'minutes' },
  { label: 'Hours', minutesMultiplier: 60, value: 'hours' },
  { label: 'Days', minutesMultiplier: 1440, value: 'days' },
  { label: 'Weeks', minutesMultiplier: 10080, value: 'weeks' },
  { label: 'Months', minutesMultiplier: 43200, value: 'months' },
]);

export const MISSION_TIMELINE_VIEW_MODES = Object.freeze({
  ACTUAL: 'actual',
  BASELINE: 'baseline',
  FORECAST: 'forecast',
});

const ACTIVE_TASK_STATUSES = new Set(['inProgress', 'running', 'blocked']);
const ACTUAL_START_EVENT_TYPES = new Set(['taskReady', 'taskInProgress', 'botRunning']);
const DURATION_UNIT_BY_VALUE = new Map(
  MISSION_DURATION_UNITS.map((unit) => [unit.value, unit])
);

/**
 * normalizeEstimatedDurationDays
 *
 * @deprecated Prefer resolveEstimatedDurationMinutes. Kept for legacy reads/tests.
 */
export function normalizeEstimatedDurationDays(value, handledBy) {
  const minutes = resolveEstimatedDurationMinutes({
    estimatedDurationDays: value,
    handledBy,
  });

  if (!minutes) return 0;

  return Math.round(minutes / 1440);
}

/**
 * resolveEstimatedDurationMinutes
 *
 * Resolves canonical task duration in minutes, migrating legacy day-only values.
 *
 * @param {object|null} task - Mission task.
 * @returns {number} Whole minutes, or 0.
 */
export function resolveEstimatedDurationMinutes(task) {
  if (String(task?.handledBy || '').trim() === MISSION_TASK_HANDLED_BY.BOT) {
    return 0;
  }

  const minutesValue = Number(task?.estimatedDurationMinutes || 0);
  if (Number.isFinite(minutesValue) && minutesValue > 0) {
    return Math.round(minutesValue);
  }

  const legacyDays = normalizeLegacyDurationDays(task?.estimatedDurationDays);
  if (legacyDays > 0) {
    return legacyDays * 1440;
  }

  return 0;
}

function normalizeLegacyDurationDays(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }

  return Math.round(parsed);
}

/**
 * convertAuthoringDurationToMinutes
 *
 * @param {number|string} amount - Author-entered amount.
 * @param {string} unit - Duration unit key.
 * @returns {number} Whole minutes.
 */
export function convertAuthoringDurationToMinutes(amount, unit) {
  const parsedAmount = Number(amount);
  if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
    return 0;
  }

  const unitDef = DURATION_UNIT_BY_VALUE.get(String(unit || '').trim()) || DURATION_UNIT_BY_VALUE.get('days');
  return Math.round(parsedAmount * unitDef.minutesMultiplier);
}

/**
 * buildDurationAuthoringDraft
 *
 * Converts stored minutes into modal-friendly amount + unit fields.
 *
 * @param {object|null} task - Mission task.
 * @returns {{ amount: string, unit: string }} Authoring draft values.
 */
export function buildDurationAuthoringDraft(task) {
  const minutes = resolveEstimatedDurationMinutes(task);
  if (!minutes) {
    return {
      amount: '',
      unit: 'days',
    };
  }

  const candidates = [...MISSION_DURATION_UNITS].reverse();
  const match = candidates.find((unitDef) => (
    minutes % unitDef.minutesMultiplier === 0
    && (minutes / unitDef.minutesMultiplier) >= 1
  ));

  if (!match) {
    return {
      amount: String(minutes),
      unit: 'minutes',
    };
  }

  return {
    amount: String(minutes / match.minutesMultiplier),
    unit: match.value,
  };
}

/**
 * formatDurationMinutes
 *
 * @param {number} minutes - Duration in minutes.
 * @returns {string} Human-readable duration label.
 */
export function formatDurationMinutes(minutes) {
  const normalizedMinutes = Math.round(Number(minutes || 0));
  if (!Number.isFinite(normalizedMinutes) || normalizedMinutes <= 0) {
    return '';
  }

  if (normalizedMinutes < 60) {
    return `${normalizedMinutes} min${normalizedMinutes === 1 ? '' : 's'}`;
  }

  if (normalizedMinutes < 1440) {
    const hours = Math.round(normalizedMinutes / 60);
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }

  if (normalizedMinutes < 10080) {
    const days = Math.round(normalizedMinutes / 1440);
    return `${days} day${days === 1 ? '' : 's'}`;
  }

  if (normalizedMinutes < 43200) {
    const weeks = Math.round(normalizedMinutes / 10080);
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  }

  const months = Math.round(normalizedMinutes / 43200);
  return `${months} month${months === 1 ? '' : 's'}`;
}

const COUNTDOWN_ACTIVE_STATUSES = new Set(['ready', 'inProgress', 'blocked', 'running']);

/**
 * resolveMissionTaskCountdownDeadlineMs
 *
 * Deadline for a live task countdown: readyAt + estimated duration,
 * with forecast / launch-day fallbacks when readyAt is missing.
 *
 * @param {object} options
 * @param {object|null} options.task
 * @param {object|null} options.missionRun
 * @param {number} [options.nowMs]
 * @returns {number} Deadline ms, or 0 when countdown should not show.
 */
export function resolveMissionTaskCountdownDeadlineMs({
  task = null,
  missionRun = null,
  nowMs = Date.now(),
} = {}) {
  const durationMinutes = resolveEstimatedDurationMinutes(task);
  if (durationMinutes <= 0) return 0;

  const status = String(task?.status || '').trim();
  if (!COUNTDOWN_ACTIVE_STATUSES.has(status)) return 0;

  const readyAtMs = Number(task?.readyAtMs || 0);
  if (Number.isFinite(readyAtMs) && readyAtMs > 0) {
    return readyAtMs + (durationMinutes * MS_PER_MINUTE);
  }

  const taskKey = String(task?.key || '').trim();
  if (taskKey && missionRun) {
    const schedule = computeMissionForecastSchedule({
      launchedAtMs: missionRun?.launchedAtMs,
      nowMs,
      taskEvents: missionRun?.taskEvents,
      tasks: missionRun?.tasks,
    });
    const plannedEndMs = Number(schedule?.taskScheduleByKey?.[taskKey]?.plannedEndMs || 0);
    if (Number.isFinite(plannedEndMs) && plannedEndMs > 0) {
      return plannedEndMs;
    }
  }

  const dayZeroMs = resolveMissionDayZeroMs(missionRun);
  if (dayZeroMs > 0) {
    return dayZeroMs + (durationMinutes * MS_PER_MINUTE);
  }

  return 0;
}

/**
 * formatMissionTaskCountdown
 *
 * Compact remaining-time label for task countdown chips.
 *
 * @param {number} deadlineMs - Target end timestamp.
 * @param {number} [nowMs]
 * @returns {{ label: string, overdue: boolean } | null}
 */
export function formatMissionTaskCountdown(deadlineMs, nowMs = Date.now()) {
  const normalizedDeadlineMs = Number(deadlineMs || 0);
  if (!Number.isFinite(normalizedDeadlineMs) || normalizedDeadlineMs <= 0) {
    return null;
  }

  const remainingMs = normalizedDeadlineMs - Number(nowMs || Date.now());
  if (remainingMs <= 0) {
    return { label: 'Overdue', overdue: true };
  }

  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const paddedSeconds = String(seconds).padStart(2, '0');
  const paddedMinutes = String(minutes).padStart(2, '0');

  // Always include minutes + seconds so the chip keeps ticking visibly.
  if (days > 0) {
    return {
      label: `${days}d ${hours}h ${paddedMinutes}m ${paddedSeconds}s`,
      overdue: false,
    };
  }
  if (hours > 0) {
    return {
      label: `${hours}h ${paddedMinutes}m ${paddedSeconds}s`,
      overdue: false,
    };
  }
  return {
    label: `${minutes}m ${paddedSeconds}s`,
    overdue: false,
  };
}

/**
 * formatMissionTaskElapsedLabel
 *
 * Human-readable elapsed duration for completed tasks (e.g. "1.5 days").
 *
 * @param {number} elapsedMs - Elapsed milliseconds.
 * @returns {string}
 */
export function formatMissionTaskElapsedLabel(elapsedMs) {
  const normalizedMs = Number(elapsedMs || 0);
  if (!Number.isFinite(normalizedMs) || normalizedMs <= 0) {
    return '';
  }

  const minutes = normalizedMs / MS_PER_MINUTE;

  if (minutes < 60) {
    const roundedMinutes = Math.max(1, Math.round(minutes));
    return `${roundedMinutes} min${roundedMinutes === 1 ? '' : 's'}`;
  }

  if (minutes < 1440) {
    const hours = minutes / 60;
    const roundedHours = hours >= 10
      ? Math.round(hours)
      : Math.round(hours * 10) / 10;
    return `${roundedHours} hour${roundedHours === 1 ? '' : 's'}`;
  }

  const days = minutes / 1440;
  const roundedDays = days >= 10
    ? Math.round(days)
    : Math.round(days * 10) / 10;
  return `${roundedDays} day${roundedDays === 1 ? '' : 's'}`;
}

/**
 * resolveMissionTaskTimeSpot
 *
 * Header time-spot presentation:
 * - waiting: "2 days allotted"
 * - active: live countdown
 * - complete: "1.5 days actual"
 *
 * @param {object} options
 * @param {object|null} options.task
 * @param {object|null} options.missionRun
 * @param {number} [options.nowMs]
 * @returns {{ label: string, mode: 'allotted'|'timer'|'actual', overdue?: boolean, live?: boolean } | null}
 */
export function resolveMissionTaskTimeSpot({
  task = null,
  missionRun = null,
  nowMs = Date.now(),
} = {}) {
  const status = String(task?.status || '').trim();
  const allottedMinutes = resolveEstimatedDurationMinutes(task);
  const allottedLabel = formatDurationMinutes(allottedMinutes);

  if (status === 'complete') {
    const { actualStartMs, actualEndMs } = computeMissionTaskActuals({
      nowMs,
      task,
      taskEvents: missionRun?.taskEvents,
    });

    let endMs = actualEndMs || Number(task?.updatedAtMs || 0) || nowMs;
    let startMs = actualStartMs;

    // Degenerate complete actuals collapse start==end when readyAt was never stamped.
    if (!startMs || startMs >= endMs) {
      startMs = Number(task?.readyAtMs || 0) || resolveMissionDayZeroMs(missionRun);
    }

    const elapsedMs = (startMs > 0 && endMs > startMs) ? (endMs - startMs) : 0;
    const elapsedLabel = formatMissionTaskElapsedLabel(elapsedMs);
    if (elapsedLabel) {
      return {
        label: `${elapsedLabel} actual`,
        live: false,
        mode: 'actual',
        overdue: false,
      };
    }

    if (allottedLabel) {
      return {
        label: `${allottedLabel} allotted`,
        live: false,
        mode: 'allotted',
        overdue: false,
      };
    }

    return null;
  }

  if (COUNTDOWN_ACTIVE_STATUSES.has(status)) {
    const deadlineMs = resolveMissionTaskCountdownDeadlineMs({
      missionRun,
      nowMs,
      task,
    });
    const countdown = formatMissionTaskCountdown(deadlineMs, nowMs);
    if (countdown?.label) {
      return {
        label: countdown.label,
        live: !countdown.overdue,
        mode: 'timer',
        overdue: Boolean(countdown.overdue),
      };
    }

    if (allottedLabel) {
      return {
        label: `${allottedLabel} allotted`,
        live: false,
        mode: 'allotted',
        overdue: false,
      };
    }

    return null;
  }

  // Waiting / not started yet — show plan duration.
  if (allottedLabel) {
    return {
      label: `${allottedLabel} allotted`,
      live: false,
      mode: 'allotted',
      overdue: false,
    };
  }

  return null;
}

/**
 * missionRunHasSchedulingDurations
 *
 * @param {object[]} tasks - Mission run or template tasks.
 * @returns {boolean} Whether projected completion can be computed.
 */
export function missionRunHasSchedulingDurations(tasks = []) {
  return (Array.isArray(tasks) ? tasks : []).some((task) => (
    resolveEstimatedDurationMinutes(task) > 0
  ));
}

/**
 * resolveMissionDayZeroMs
 *
 * @param {object|null} missionRun - Normalized Mission run.
 * @returns {number} Positive millisecond timestamp or 0.
 */
export function resolveMissionDayZeroMs(missionRun) {
  const launchedAtMs = Number(missionRun?.launchedAtMs || 0);
  if (Number.isFinite(launchedAtMs) && launchedAtMs > 0) {
    return launchedAtMs;
  }

  const updatedAtMs = Number(missionRun?.updatedAtMs || 0);
  if (Number.isFinite(updatedAtMs) && updatedAtMs > 0) {
    return updatedAtMs;
  }

  return 0;
}

/**
 * computeMinutesBetweenMs
 *
 * @param {number} startMs - Start timestamp.
 * @param {number} endMs - End timestamp.
 * @returns {number} Whole minutes elapsed, rounded up.
 */
export function computeMinutesBetweenMs(startMs, endMs) {
  const normalizedStartMs = Number(startMs || 0);
  const normalizedEndMs = Number(endMs || 0);

  if (!Number.isFinite(normalizedStartMs) || !Number.isFinite(normalizedEndMs)) {
    return 0;
  }

  if (normalizedEndMs <= normalizedStartMs) {
    return 0;
  }

  return Math.ceil((normalizedEndMs - normalizedStartMs) / MS_PER_MINUTE);
}

/**
 * computeDaysBetweenMs
 *
 * @deprecated Prefer computeMinutesBetweenMs + formatDurationMinutes.
 */
export function computeDaysBetweenMs(startMs, endMs) {
  return Math.ceil(computeMinutesBetweenMs(startMs, endMs) / 1440);
}

function buildPrerequisiteKeysFromEdges(edges = []) {
  const byToTask = new Map();
  (Array.isArray(edges) ? edges : []).forEach((edge) => {
    const fromTaskKey = String(edge?.fromTaskKey || '').trim();
    const toTaskKey = String(edge?.toTaskKey || '').trim();
    if (!fromTaskKey || !toTaskKey) return;
    const list = byToTask.get(toTaskKey) || [];
    if (!list.includes(fromTaskKey)) list.push(fromTaskKey);
    byToTask.set(toTaskKey, list);
  });
  return byToTask;
}

function buildTopologicalTaskOrder(tasks = [], edges = null) {
  const edgePrereqs = Array.isArray(edges) && edges.length > 0
    ? buildPrerequisiteKeysFromEdges(edges)
    : null;

  const normalizedTasks = (Array.isArray(tasks) ? tasks : [])
    .map((task) => {
      const key = String(task?.key || '').trim();
      const prerequisiteTaskKeys = edgePrereqs
        ? (edgePrereqs.get(key) || [])
        : (Array.isArray(task?.prerequisiteTaskKeys) ? task.prerequisiteTaskKeys : []);
      return {
        key,
        prerequisiteTaskKeys,
        task,
      };
    })
    .filter((entry) => entry.key);

  const keySet = new Set(normalizedTasks.map((entry) => entry.key));
  const inDegree = new Map();
  const dependentsByKey = new Map();

  normalizedTasks.forEach(({ key }) => {
    inDegree.set(key, 0);
    dependentsByKey.set(key, []);
  });

  normalizedTasks.forEach(({ key, prerequisiteTaskKeys }) => {
    const validPrerequisites = prerequisiteTaskKeys
      .map((entry) => String(entry || '').trim())
      .filter((entry) => entry && entry !== key && keySet.has(entry));

    inDegree.set(key, validPrerequisites.length);

    validPrerequisites.forEach((prerequisiteKey) => {
      dependentsByKey.get(prerequisiteKey).push(key);
    });
  });

  const queue = normalizedTasks
    .filter(({ key }) => inDegree.get(key) === 0)
    .map(({ key }) => key);
  const order = [];

  while (queue.length > 0) {
    const currentKey = queue.shift();
    order.push(currentKey);

    (dependentsByKey.get(currentKey) || []).forEach((dependentKey) => {
      const nextInDegree = (inDegree.get(dependentKey) || 0) - 1;
      inDegree.set(dependentKey, nextInDegree);

      if (nextInDegree === 0) {
        queue.push(dependentKey);
      }
    });
  }

  return {
    hasCycle: order.length !== normalizedTasks.length,
    order,
    taskByKey: new Map(normalizedTasks.map((entry) => [entry.key, entry.task])),
  };
}

/**
 * computeMissionTopologicalTaskOrder
 *
 * Returns workflow tasks in dependency order (edges when provided, else legacy prereqs).
 *
 * @param {object[]} tasks - Mission run or template tasks.
 * @param {object[]|null} [edges] - Workflow edges.
 * @returns {{ hasCycle: boolean, order: string[], taskByKey: Map<string, object> }} Topological order metadata.
 */
export function computeMissionTopologicalTaskOrder(tasks = [], edges = null) {
  return buildTopologicalTaskOrder(tasks, edges);
}

function buildEmptySchedule(dayZeroMs = 0, hasCycle = false) {
  return {
    dayZeroMs,
    hasCycle,
    projectedCompletionMs: dayZeroMs,
    projectedDurationMinutes: 0,
    taskScheduleByKey: {},
  };
}

function buildForwardSchedule({
  dayZeroMs,
  edges = null,
  getPredecessorEndMs,
  order,
  taskByKey,
}) {
  const taskScheduleByKey = {};
  let projectedCompletionMs = dayZeroMs;
  const edgePrereqs = Array.isArray(edges) && edges.length > 0
    ? buildPrerequisiteKeysFromEdges(edges)
    : null;

  order.forEach((taskKey) => {
    const task = taskByKey.get(taskKey);
    const plannedDurationMinutes = resolveEstimatedDurationMinutes(task);
    const prerequisiteKeys = (
      edgePrereqs
        ? (edgePrereqs.get(taskKey) || [])
        : (Array.isArray(task?.prerequisiteTaskKeys) ? task.prerequisiteTaskKeys : [])
    )
      .map((entry) => String(entry || '').trim())
      .filter((entry) => entry && entry !== taskKey && taskScheduleByKey[entry]);

    const predecessorEnds = prerequisiteKeys
      .map((entry) => getPredecessorEndMs(entry, taskScheduleByKey[entry], taskByKey.get(entry)))
      .filter((value) => Number.isFinite(value) && value > 0);

    const plannedStartMs = predecessorEnds.length > 0
      ? Math.max(...predecessorEnds)
      : dayZeroMs;
    const plannedEndMs = plannedStartMs + (plannedDurationMinutes * MS_PER_MINUTE);

    taskScheduleByKey[taskKey] = {
      plannedDurationMinutes,
      plannedEndMs,
      plannedStartMs,
      taskKey,
    };

    projectedCompletionMs = Math.max(projectedCompletionMs, plannedEndMs);
  });

  return {
    dayZeroMs,
    hasCycle: false,
    projectedCompletionMs,
    projectedDurationMinutes: computeMinutesBetweenMs(dayZeroMs, projectedCompletionMs),
    taskScheduleByKey,
  };
}

/**
 * computeMissionBaselineSchedule
 *
 * @param {object} options - Schedule inputs.
 * @returns {object} Baseline schedule summary and per-task entries.
 */
export function computeMissionBaselineSchedule({ edges = null, launchedAtMs, tasks = [] } = {}) {
  const dayZeroMs = Number(launchedAtMs || 0);

  if (!Number.isFinite(dayZeroMs) || dayZeroMs <= 0) {
    return buildEmptySchedule(0, false);
  }

  const { hasCycle, order, taskByKey } = buildTopologicalTaskOrder(tasks, edges);

  if (hasCycle) {
    return buildEmptySchedule(dayZeroMs, true);
  }

  return buildForwardSchedule({
    dayZeroMs,
    edges,
    getPredecessorEndMs: (_taskKey, scheduleEntry) => scheduleEntry?.plannedEndMs || 0,
    order,
    taskByKey,
  });
}

/**
 * computeMissionDerivedSchedule
 *
 * Backward-compatible alias for baseline scheduling.
 */
export function computeMissionDerivedSchedule(options = {}) {
  return computeMissionBaselineSchedule(options);
}

/**
 * computeMissionForecastSchedule
 *
 * Replans downstream tasks using actual completion times where available.
 *
 * @param {object} options - Forecast inputs.
 * @returns {object} Forecast schedule summary and per-task entries.
 */
export function computeMissionForecastSchedule({
  edges = null,
  launchedAtMs,
  nowMs = Date.now(),
  taskEvents = [],
  tasks = [],
} = {}) {
  const dayZeroMs = Number(launchedAtMs || 0);

  if (!Number.isFinite(dayZeroMs) || dayZeroMs <= 0) {
    return buildEmptySchedule(0, false);
  }

  const { hasCycle, order, taskByKey } = buildTopologicalTaskOrder(tasks, edges);

  if (hasCycle) {
    return buildEmptySchedule(dayZeroMs, true);
  }

  return buildForwardSchedule({
    dayZeroMs,
    edges,
    getPredecessorEndMs: (taskKey, scheduleEntry, task) => {
      if (String(task?.status || '').trim() === 'complete') {
        const { actualEndMs } = computeMissionTaskActuals({ nowMs, task, taskEvents });
        if (actualEndMs > 0) {
          return actualEndMs;
        }
      }

      return scheduleEntry?.plannedEndMs || 0;
    },
    order,
    taskByKey,
  });
}

/**
 * computeMissionTaskActuals
 *
 * @param {object} options - Actuals inputs.
 * @returns {object} Actual start/end timestamps for the task.
 */
export function computeMissionTaskActuals({ task, taskEvents = [], nowMs = Date.now() } = {}) {
  const taskKey = String(task?.key || '').trim();
  const status = String(task?.status || '').trim();
  const relevantEvents = (Array.isArray(taskEvents) ? taskEvents : [])
    .filter((event) => String(event?.taskKey || '').trim() === taskKey)
    .sort((left, right) => Number(left?.createdAtMs || 0) - Number(right?.createdAtMs || 0));

  let actualStartMs = Number(task?.readyAtMs || 0);
  if (!Number.isFinite(actualStartMs) || actualStartMs <= 0) {
    actualStartMs = 0;
  }

  if (!actualStartMs) {
    const startEvent = relevantEvents.find((event) => ACTUAL_START_EVENT_TYPES.has(String(event?.type || '').trim()));
    actualStartMs = Number(startEvent?.createdAtMs || 0) || 0;
  }

  const completeEvent = relevantEvents.find((event) => String(event?.type || '').trim() === 'taskComplete');
  let actualEndMs = Number(completeEvent?.createdAtMs || 0) || 0;

  if (!actualEndMs && ACTIVE_TASK_STATUSES.has(status)) {
    actualEndMs = nowMs;
  }

  if (status === 'complete' && actualEndMs > 0 && (!actualStartMs || actualStartMs > actualEndMs)) {
    actualStartMs = actualEndMs;
  }

  if (actualEndMs > 0 && actualStartMs > 0 && actualEndMs < actualStartMs) {
    actualStartMs = actualEndMs;
  }

  return {
    actualEndMs: actualEndMs > 0 ? actualEndMs : 0,
    actualStartMs: actualStartMs > 0 ? actualStartMs : 0,
    hasActual: actualStartMs > 0 || actualEndMs > 0,
  };
}

/**
 * getMissionCompletionElapsedMs
 *
 * @param {object|null} missionRun - Normalized Mission run.
 * @returns {number} Elapsed milliseconds or 0.
 */
export function getMissionCompletionElapsedMs(missionRun) {
  const dayZeroMs = resolveMissionDayZeroMs(missionRun);
  if (!dayZeroMs) return 0;

  const tasks = Array.isArray(missionRun?.tasks) ? missionRun.tasks : [];
  const taskEvents = Array.isArray(missionRun?.taskEvents) ? missionRun.taskEvents : [];
  let latestCompleteMs = 0;

  tasks.forEach((task) => {
    const { actualEndMs } = computeMissionTaskActuals({ task, taskEvents });
    latestCompleteMs = Math.max(latestCompleteMs, actualEndMs);
  });

  if (latestCompleteMs > dayZeroMs) {
    return latestCompleteMs - dayZeroMs;
  }

  const updatedAtMs = Number(missionRun?.updatedAtMs || 0);
  if (Number.isFinite(updatedAtMs) && updatedAtMs > dayZeroMs) {
    return updatedAtMs - dayZeroMs;
  }

  return 0;
}

function buildScheduleDurationSummary(minutes, prefix) {
  const durationLabel = formatDurationMinutes(minutes);
  if (!durationLabel) return '';
  return `${prefix}${durationLabel}`;
}

/**
 * formatMissionScheduleSummary
 *
 * @param {object} options - Summary inputs.
 * @param {string} [options.viewMode='forecast'] - Timeline mode for active runs.
 * @returns {string} Summary copy or empty string.
 */
export function formatMissionScheduleSummary({
  missionRun,
  missionStatus,
  viewMode = MISSION_TIMELINE_VIEW_MODES.FORECAST,
} = {}) {
  if (!missionRun) return '';

  const dayZeroMs = resolveMissionDayZeroMs(missionRun);
  if (!dayZeroMs) return '';

  const tasks = Array.isArray(missionRun?.tasks) ? missionRun.tasks : [];
  const taskEvents = Array.isArray(missionRun?.taskEvents) ? missionRun.taskEvents : [];

  if (viewMode === MISSION_TIMELINE_VIEW_MODES.ACTUAL) {
    if (missionStatus !== 'complete') {
      return '';
    }

    const elapsedMinutes = computeMinutesBetweenMs(
      dayZeroMs,
      dayZeroMs + getMissionCompletionElapsedMs(missionRun)
    );

    if (elapsedMinutes <= 0) {
      return 'Completed on launch day';
    }

    return `Completed in ${formatDurationMinutes(elapsedMinutes)}`;
  }

  if (missionStatus === 'complete') {
    const elapsedMinutes = computeMinutesBetweenMs(
      dayZeroMs,
      dayZeroMs + getMissionCompletionElapsedMs(missionRun)
    );

    if (elapsedMinutes <= 0) {
      return 'Completed on launch day';
    }

    return `Completed in ${formatDurationMinutes(elapsedMinutes)}`;
  }

  if (!missionRunHasSchedulingDurations(tasks)) {
    return '';
  }

  const schedule = viewMode === MISSION_TIMELINE_VIEW_MODES.BASELINE
    ? computeMissionBaselineSchedule({ launchedAtMs: dayZeroMs, tasks })
    : computeMissionForecastSchedule({
      launchedAtMs: dayZeroMs,
      taskEvents,
      tasks,
    });

  if (schedule.hasCycle || schedule.projectedDurationMinutes <= 0) {
    return '';
  }

  if (viewMode === MISSION_TIMELINE_VIEW_MODES.BASELINE) {
    const durationLabel = buildScheduleDurationSummary(
      schedule.projectedDurationMinutes,
      'Original plan: ~'
    );
    return durationLabel ? `${durationLabel} from launch` : '';
  }

  const durationLabel = buildScheduleDurationSummary(
    schedule.projectedDurationMinutes,
    'Projected completion: ~'
  );
  return durationLabel ? `${durationLabel} from launch` : '';
}

/**
 * formatMissionEndDateLabel
 *
 * Friendly calendar label for projected / actual end dates.
 *
 * @param {number} timestampMs
 * @returns {string}
 */
export function formatMissionEndDateLabel(timestampMs) {
  const normalizedMs = Number(timestampMs || 0);
  if (!Number.isFinite(normalizedMs) || normalizedMs <= 0) return '';

  try {
    return new Date(normalizedMs).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return '';
  }
}

function hasIncompletePeopleTaskWithoutDuration(tasks = []) {
  return (Array.isArray(tasks) ? tasks : []).some((task) => {
    if (String(task?.handledBy || '').trim() === MISSION_TASK_HANDLED_BY.BOT) {
      return false;
    }
    const status = String(task?.status || '').trim();
    if (status === 'complete') return false;
    return resolveEstimatedDurationMinutes(task) <= 0;
  });
}

function formatPlanDeltaLabel(deltaMinutes) {
  const signedMinutes = Math.round(Number(deltaMinutes || 0));
  if (!Number.isFinite(signedMinutes)) return '';

  const absMinutes = Math.abs(signedMinutes);
  if (absMinutes < 60) {
    return 'On plan';
  }

  const durationLabel = formatDurationMinutes(absMinutes);
  if (!durationLabel) return 'On plan';

  if (signedMinutes < 0) {
    return `${durationLabel} early`;
  }
  return `${durationLabel} late`;
}

/**
 * resolveMissionElapsedPresentation
 *
 * Meta elapsed-time chip for live workflow runs (under task progress).
 *
 * Active: time since launch. Complete: launch → finish duration.
 *
 * @param {object} options
 * @param {object|null} options.missionRun
 * @param {string} [options.missionStatus]
 * @param {number} [options.nowMs]
 * @returns {{ secondary: string } | null}
 */
export function resolveMissionElapsedPresentation({
  missionRun = null,
  missionStatus = '',
  nowMs = Date.now(),
} = {}) {
  if (!missionRun) return null;

  const dayZeroMs = resolveMissionDayZeroMs(missionRun);
  if (!dayZeroMs) return null;

  const status = String(missionStatus || getMissionRunStatusFallback(missionRun)).trim();
  let endMs = Number(nowMs || Date.now());

  if (status === 'complete') {
    const completionElapsedMs = getMissionCompletionElapsedMs(missionRun);
    endMs = completionElapsedMs > 0 ? dayZeroMs + completionElapsedMs : dayZeroMs;
  }

  const elapsedMs = Math.max(0, endMs - dayZeroMs);
  const elapsedLabel = formatMissionTaskElapsedLabel(elapsedMs);
  if (!elapsedLabel) return null;

  return {
    secondary: `${elapsedLabel} elapsed`,
  };
}

/**
 * resolveMissionEndDatePresentation
 *
 * Meta end-date chip for live workflow runs.
 *
 * Active: Est. Wed, Dec 12 (+ ~ remaining). Uses "~" on the date when some
 * incomplete people steps lack duration. Complete: Finished date (+ early/late).
 * Hidden when no people-task durations exist on an active run.
 *
 * @param {object} options
 * @param {object|null} options.missionRun
 * @param {string} [options.missionStatus]
 * @param {number} [options.nowMs]
 * @returns {{ primary: string, secondary: string, approximate: boolean } | null}
 */
export function resolveMissionEndDatePresentation({
  missionRun = null,
  missionStatus = '',
  nowMs = Date.now(),
} = {}) {
  if (!missionRun) return null;

  const dayZeroMs = resolveMissionDayZeroMs(missionRun);
  if (!dayZeroMs) return null;

  const tasks = Array.isArray(missionRun?.tasks) ? missionRun.tasks : [];
  const taskEvents = Array.isArray(missionRun?.taskEvents) ? missionRun.taskEvents : [];
  const status = String(missionStatus || getMissionRunStatusFallback(missionRun)).trim();

  if (status === 'complete') {
    const elapsedMs = getMissionCompletionElapsedMs(missionRun);
    const actualEndMs = elapsedMs > 0 ? dayZeroMs + elapsedMs : dayZeroMs;
    const finishedLabel = formatMissionEndDateLabel(actualEndMs);
    if (!finishedLabel) return null;

    let secondary = '';
    if (missionRunHasSchedulingDurations(tasks)) {
      const baseline = computeMissionBaselineSchedule({
        launchedAtMs: dayZeroMs,
        tasks,
      });
      const baselineEndMs = Number(baseline?.projectedCompletionMs || 0);
      if (baselineEndMs > 0) {
        secondary = formatPlanDeltaLabel(
          Math.round((actualEndMs - baselineEndMs) / MS_PER_MINUTE)
        );
      }
    }

    return {
      approximate: false,
      primary: `Finished ${finishedLabel}`,
      secondary,
    };
  }

  if (!missionRunHasSchedulingDurations(tasks)) {
    return null;
  }

  const forecast = computeMissionForecastSchedule({
    launchedAtMs: dayZeroMs,
    nowMs,
    taskEvents,
    tasks,
  });

  const projectedCompletionMs = Number(forecast?.projectedCompletionMs || 0);
  if (!projectedCompletionMs || forecast?.hasCycle) {
    return null;
  }

  const dateLabel = formatMissionEndDateLabel(projectedCompletionMs);
  if (!dateLabel) return null;

  const approximate = hasIncompletePeopleTaskWithoutDuration(tasks);
  const remainingMinutes = Math.max(
    0,
    Math.round((projectedCompletionMs - nowMs) / MS_PER_MINUTE)
  );
  const remainingLabel = formatDurationMinutes(remainingMinutes);

  return {
    approximate,
    primary: approximate ? `Est. Completion ~${dateLabel}` : `Est. Completion ${dateLabel}`,
    secondary: remainingLabel ? `~${remainingLabel} remaining` : '',
  };
}

function getMissionRunStatusFallback(missionRun) {
  const tasks = Array.isArray(missionRun?.tasks) ? missionRun.tasks : [];
  if (!tasks.length) return 'notStarted';
  if (tasks.every((task) => String(task?.status || '').trim() === 'complete')) {
    return 'complete';
  }
  return 'inProgress';
}

/**
 * formatMissionTemplateScheduleSummary
 *
 * @param {object} options - Summary inputs.
 * @returns {string} Summary copy or empty string.
 */
export function formatMissionTemplateScheduleSummary({ template } = {}) {
  const tasks = Array.isArray(template?.tasks) ? template.tasks : [];

  if (!tasks.length || !missionRunHasSchedulingDurations(tasks)) {
    return '';
  }

  const schedule = computeMissionBaselineSchedule({
    launchedAtMs: MS_PER_DAY,
    tasks,
  });

  if (schedule.hasCycle || schedule.projectedDurationMinutes <= 0) {
    return '';
  }

  return `${buildScheduleDurationSummary(schedule.projectedDurationMinutes, 'Estimated completion: ~')} after launch`;
}

/**
 * computeMissionTimelineRange
 *
 * @param {object} options - Range inputs.
 * @returns {object} Timeline start/end timestamps.
 */
export function computeMissionTimelineRange({
  baselineSchedule,
  forecastSchedule,
  nowMs = Date.now(),
  taskEvents = [],
  tasks = [],
  viewMode = MISSION_TIMELINE_VIEW_MODES.BASELINE,
} = {}) {
  const startMs = Number(
    baselineSchedule?.dayZeroMs
    || forecastSchedule?.dayZeroMs
    || 0
  );

  if (!Number.isFinite(startMs) || startMs <= 0) {
    return {
      endMs: 0,
      startMs: 0,
    };
  }

  let endMs = startMs;

  if (viewMode === MISSION_TIMELINE_VIEW_MODES.ACTUAL) {
    endMs = Math.max(startMs, nowMs);

    (Array.isArray(tasks) ? tasks : []).forEach((task) => {
      const { actualEndMs } = computeMissionTaskActuals({ nowMs, task, taskEvents });
      if (actualEndMs > endMs) {
        endMs = actualEndMs;
      }
    });
  } else if (viewMode === MISSION_TIMELINE_VIEW_MODES.FORECAST) {
    endMs = Math.max(
      startMs,
      Number(forecastSchedule?.projectedCompletionMs || startMs),
      nowMs
    );
  } else {
    endMs = Math.max(
      startMs,
      Number(baselineSchedule?.projectedCompletionMs || startMs),
      nowMs
    );
  }

  const rangeMs = endMs - startMs;
  const paddingMs = Math.max(MS_PER_MINUTE * 15, Math.round(rangeMs * 0.08));

  return {
    endMs: endMs + paddingMs,
    startMs,
  };
}

/**
 * computeMissionTimelineAxis
 *
 * Builds adaptive axis ticks for the visible timeline range.
 *
 * @param {object} options - Axis inputs.
 * @returns {object} Axis metadata and tick labels.
 */
export function computeMissionTimelineAxis({ endMs, startMs } = {}) {
  const normalizedStartMs = Number(startMs || 0);
  const normalizedEndMs = Number(endMs || 0);
  const rangeMs = normalizedEndMs - normalizedStartMs;

  if (!Number.isFinite(normalizedStartMs) || !Number.isFinite(normalizedEndMs) || rangeMs <= 0) {
    return {
      ticks: [],
      unit: 'days',
    };
  }

  let unit = 'days';
  let stepMs = MS_PER_DAY;
  let maxTicks = 8;

  if (rangeMs <= 6 * MS_PER_HOUR) {
    unit = 'minutes';
    stepMs = rangeMs <= 2 * MS_PER_HOUR ? 15 * MS_PER_MINUTE : 30 * MS_PER_MINUTE;
  } else if (rangeMs <= 3 * MS_PER_DAY) {
    unit = 'hours';
    stepMs = rangeMs <= MS_PER_DAY ? MS_PER_HOUR : 6 * MS_PER_HOUR;
  } else if (rangeMs <= 8 * MS_PER_WEEK) {
    unit = 'days';
    stepMs = rangeMs <= 14 * MS_PER_DAY ? MS_PER_DAY : 2 * MS_PER_DAY;
  } else {
    unit = 'weeks';
    stepMs = MS_PER_WEEK;
    maxTicks = 6;
  }

  const ticks = [];
  const tickCount = Math.min(maxTicks, Math.max(2, Math.ceil(rangeMs / stepMs) + 1));
  const adjustedStepMs = Math.max(stepMs, Math.ceil(rangeMs / Math.max(tickCount - 1, 1)));

  for (let offsetMs = 0; offsetMs <= rangeMs; offsetMs += adjustedStepMs) {
    ticks.push({
      label: formatMissionTimelineTick(normalizedStartMs + offsetMs, normalizedStartMs, unit),
      leftPercent: (offsetMs / rangeMs) * 100,
    });
  }

  const lastTick = ticks[ticks.length - 1];
  if (!lastTick || lastTick.leftPercent < 100) {
    ticks.push({
      label: formatMissionTimelineTick(normalizedEndMs, normalizedStartMs, unit),
      leftPercent: 100,
    });
  }

  return {
    ticks,
    unit,
  };
}

/**
 * formatMissionTimelineTick
 *
 * @param {number} tickMs - Tick timestamp.
 * @param {number} originMs - Timeline origin timestamp.
 * @param {string} unit - Axis unit key.
 * @returns {string} Tick label.
 */
export function formatMissionTimelineTick(tickMs, originMs, unit) {
  const offsetMinutes = computeMinutesBetweenMs(originMs, tickMs);

  if (unit === 'minutes') {
    if (offsetMinutes <= 0) return 'Launch';
    return `+${offsetMinutes}m`;
  }

  if (unit === 'hours') {
    if (offsetMinutes <= 0) return 'Launch';
    const hours = Math.round(offsetMinutes / 60);
    return hours === 0 ? 'Launch' : `+${hours}h`;
  }

  if (unit === 'weeks') {
    const weeks = Math.round(offsetMinutes / 10080);
    return weeks <= 0 ? 'Wk 0' : `Wk ${weeks}`;
  }

  const days = Math.round(offsetMinutes / 1440);
  return days <= 0 ? 'Day 0' : `Day ${days}`;
}

/**
 * computeTimelineBarMetrics
 *
 * @param {object} options - Bar inputs.
 * @returns {object|null} Percent-based bar metrics.
 */
export function computeTimelineBarMetrics({
  endMs,
  rangeEndMs,
  rangeStartMs,
  startMs,
} = {}) {
  const normalizedStartMs = Number(startMs || 0);
  const normalizedEndMs = Number(endMs || 0);
  const normalizedRangeStartMs = Number(rangeStartMs || 0);
  const normalizedRangeEndMs = Number(rangeEndMs || 0);
  const rangeMs = normalizedRangeEndMs - normalizedRangeStartMs;

  if (!Number.isFinite(normalizedStartMs) || !Number.isFinite(normalizedEndMs) || rangeMs <= 0) {
    return null;
  }

  const clampedStartMs = Math.max(normalizedStartMs, normalizedRangeStartMs);
  const clampedEndMs = Math.max(clampedStartMs, normalizedEndMs);
  const leftPercent = ((clampedStartMs - normalizedRangeStartMs) / rangeMs) * 100;
  const widthPercent = ((clampedEndMs - clampedStartMs) / rangeMs) * 100;

  return {
    isMilestone: widthPercent <= 0.5,
    leftPercent,
    widthPercent: Math.max(widthPercent, 0.5),
  };
}
